import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { RecentSearchItem } from "../types";
import { History, SearchBadge } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import { LoadingState } from "@/components/GlobalSearch/components/LoadingState";
import Text from "@/components/ui/text";
import { RecentSearchItem as RecentSearchItemComponent } from "./RecentSearchItem";

interface RecentSearchesProps {
  recentSearches: RecentSearchItem[];
  onRecentSearchClick: (item: RecentSearchItem) => void;
  onRecentSearchOpenInNewTab?: (item: RecentSearchItem) => void;
  isLoading?: boolean;
  selectedIndex?: number;
  hasKeyboardNavigation?: boolean;
}

export const RecentSearches: React.FC<RecentSearchesProps> = ({
  recentSearches,
  onRecentSearchClick,
  onRecentSearchOpenInNewTab,
  isLoading = false,
  selectedIndex = -1,
  hasKeyboardNavigation = false
}) => {
  const { t } = useTranslation();
  const selectedItemRef = useRef<HTMLDivElement>(null);

  // Scroll selected item into view when selectedIndex changes
  useEffect(() => {
    if (selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest"
      });
    }
  }, [selectedIndex]);

  if (isLoading) {
    return <LoadingState />;
  }

  if (recentSearches.length === 0) {
    return (
      <div
        data-test="global-search-recent-empty"
        className="h-full px-4 py-6 flex flex-col items-center justify-center"
      >
        <SearchBadge />
        <Text variant="smBodySecondary">
          {t("globalSearch.recentSearches.empty")}
        </Text>
      </div>
    );
  }

  return (
    <div
      data-test="global-search-recent-searches"
      className="h-full flex flex-col"
    >
      <div>
        <div
          data-test="global-search-recent-header"
          className="flex items-center gap-2 mb-3 p-2"
        >
          <IconContainer size="5" color="primary">
            <History />
          </IconContainer>
          <Text variant="body">{t("globalSearch.recentSearches.title")}</Text>
        </div>
      </div>
      <div
        data-test="global-search-recent-list"
        className="flex-1 overflow-y-auto -mr-4 pr-4"
        role="listbox"
        aria-label={t("globalSearch.recentSearches.ariaLabel")}
      >
        {recentSearches.map((item, index) => (
          <RecentSearchItemComponent
            key={item.id}
            item={item}
            isSelected={hasKeyboardNavigation && index === selectedIndex}
            onClick={() => onRecentSearchClick(item)}
            onOpenInNewTab={
              onRecentSearchOpenInNewTab
                ? () => onRecentSearchOpenInNewTab(item)
                : undefined
            }
            ref={
              hasKeyboardNavigation && index === selectedIndex
                ? selectedItemRef
                : null
            }
          />
        ))}
      </div>
    </div>
  );
};
